import api from 'api'
import axios, { AxiosRequestConfig } from 'axios'
import { subMinutes } from 'date-fns'
import { useEffect, useState } from 'react'

import { CHAMADA_PAINEL_TIMERS } from '../constants-chamadaPainel'
import { Chamada } from '../model-chamadaPainel'

export interface UsePainelSessionProps {
  tokenAcesso: string
  uuidDispositivo: string
}

export interface UsePainelSessionResult {
  hasError: boolean
  isAuthenticated: boolean
  nomeUnidadeSaude: string
  chamadasIniciais: Chamada[] | null
  atualizarChamadas: (from: Date, config?: AxiosRequestConfig) => Promise<Chamada[] | null>
}

const atualizarChamadas = async (from: Date, options?: AxiosRequestConfig): Promise<Chamada[] | null> => {
  const response = await api.chamadaPainel.chamadas(from, options)
  return response.data
}

export function usePainelChamadasSession(props: Readonly<UsePainelSessionProps>): UsePainelSessionResult {
  const { tokenAcesso, uuidDispositivo } = props

  const [hasError, setHasError] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [nomeUnidadeSaude, setNomeUnidadeSaude] = useState('')
  const [chamadasIniciais, setChamadasIniciais] = useState<Chamada[] | null>(null)

  useEffect(() => {
    const source = axios.CancelToken.source()
    let timeoutId: NodeJS.Timeout

    const checkStatus = async () => {
      try {
        const response = await api.chamadaPainel.status({ cancelToken: source.token })
        return response.data
      } catch (error) {
        if (axios.isCancel(error)) throw error
        return null
      }
    }

    const authenticate = async () => {
      try {
        await api.chamadaPainel.login({ tokenAcesso, uuidDispositivo }, { cancelToken: source.token })
        return true
      } catch (error) {
        if (axios.isCancel(error)) throw error
        return false
      }
    }

    const scheduleKeepAlive = () => {
      timeoutId = setTimeout(async () => {
        try {
          const stillValid = await checkStatus()

          if (!stillValid) {
            const recovered = await authenticate()
            if (!recovered) {
              setIsAuthenticated(false)
              setHasError(true)
              return
            }
            setIsAuthenticated(true)
          }

          scheduleKeepAlive()
        } catch {
          return
        }
      }, CHAMADA_PAINEL_TIMERS.KEEP_ALIVE_INTERVAL)
    }

    const initializeSession = async () => {
      try {
        let statusData = await checkStatus()

        if (!statusData) {
          const loginOk = await authenticate()
          if (loginOk) {
            statusData = await checkStatus()
          }
        }

        if (statusData) {
          setNomeUnidadeSaude(statusData.nomeUnidadeSaude)
          setIsAuthenticated(true)

          const data = await atualizarChamadas(
            subMinutes(new Date(), CHAMADA_PAINEL_TIMERS.HISTORICO_CHAMADAS_MINUTOS),
            { cancelToken: source.token }
          )
          if (data) {
            setChamadasIniciais(data)
          }

          setHasError(false)
          scheduleKeepAlive()
        } else {
          setIsAuthenticated(false)
          setHasError(true)
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          return
        }
        setIsAuthenticated(false)
        setHasError(true)
      }
    }

    initializeSession()

    return () => {
      source.cancel()
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [tokenAcesso, uuidDispositivo])

  return { hasError, isAuthenticated, nomeUnidadeSaude, chamadasIniciais, atualizarChamadas }
}
